import api from "./api";
import apiExtensions from "./api.extensions";
import { generateUniqueImageName } from "./imageService";
import localforage from "localforage";
import { useDocumentStore } from "../store/documentState";
import { useDealsStore } from "../store/dealsStore";
import { getSignedUrl, updateDealTextProperty } from "./hubspotBackendClient";
import { DOCUMENT_TO_PROPERTY_MAP } from "./hubspotDealMapper";
import { AbschlussberichtDraft, AbschlussberichtSubmission } from "../types/abschlussbericht";
import { resolvePhaseFolderName } from "../utils/phaseFolder";
import { UploadedDocumentFile } from "../types/documents";
import {
  normalizeUploadedDocumentFiles,
  uploadedDocumentFilesToPropertyValue,
} from "../utils/uploadedDocumentFiles";
import {
  ProjektdokumentationFormData,
  ProjektdokumentationStatusUpdate,
} from "../types/projektdokumentation";

// Upload-Queue-Service für Offline-Funktionalität

// IndexedDB Store konfigurieren
const queueStore = localforage.createInstance({
  name: "360volt-queue",
  storeName: "upload-queue",
});

/**
 * Queue-Item-Typen
 */
export interface QueueItem {
  id: string;
  type: string;
  data: any;
  retries: number;
  createdAt: Date;
}

interface QueuedUploadFile {
  id?: string;
  name?: string;
  type?: string;
  size?: number;
  dataUrl: string;
}

// Make.com Webhook (PDF-Generation + Upload)
// Hinweis: Der Sync läuft im Browser (online). Offline wird nur in IndexedDB gequeued.
const MAKE_PDF_WEBHOOK_URL =
  "https://hook.eu2.make.com/zgaipwc0s1br919111qbfl5fcl6t1ao1";

/**
 * Queue-Verarbeitungsergebnis
 */
interface ProcessResult {
  successful: string[]; // IDs der erfolgreichen Items
  failed: string[]; // IDs der fehlgeschlagenen Items
}

const notifyProjectStatusRefresh = (successfulIds: string[]) => {
  if (typeof window === "undefined" || successfulIds.length === 0) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("project-status-refresh", {
      detail: { successfulIds },
    })
  );
};

/**
 * Timeout-Helper: Promise mit Zeitlimit
 */
const timeoutPromise = (ms: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Timeout after ${ms}ms`)), ms);
  });
};

/**
 * Queue aus IndexedDB laden
 */
export const loadQueue = async (): Promise<QueueItem[]> => {
  try {
    // Alle Queue-Items aus IndexedDB laden
    const keys = await queueStore.keys();
    const items: QueueItem[] = [];

    for (const key of keys) {
      const item = await queueStore.getItem<QueueItem>(key);
      if (item) {
        items.push(item);
      }
    }

    return items;
  } catch (error) {
    console.error("Fehler beim Laden der Queue aus IndexedDB:", error);
    return [];
  }
};

/**
 * Queue-Item zur IndexedDB hinzufügen
 */
export const addQueueItem = async (item: QueueItem): Promise<void> => {
  try {
    await queueStore.setItem(item.id, item);
  } catch (error) {
    console.error("Fehler beim Speichern des Queue-Items in IndexedDB:", error);
  }
};

/**
 * Queue-Item aus IndexedDB entfernen
 */
export const removeQueueItem = async (id: string): Promise<void> => {
  try {
    await queueStore.removeItem(id);
  } catch (error) {
    console.error(
      "Fehler beim Entfernen des Queue-Items aus IndexedDB:",
      error
    );
  }
};

/**
 * Queue-Items verarbeiten mit Timeout pro Item
 */
export const processQueue = async (
  items: QueueItem[]
): Promise<ProcessResult> => {
  const successful: string[] = [];
  const failed: string[] = [];

  // Items verarbeiten, die noch nicht zu oft wiederholt wurden
  const validItems = items.filter((item) => item.retries < 5);

  // Keine Items zu verarbeiten
  if (validItems.length === 0) {
    return { successful, failed };
  }

  // Offline-Erkennung
  if (!navigator.onLine) {
    validItems.forEach((item) => failed.push(item.id));
    return { successful, failed };
  }

  // Items mit Promise.allSettled + Timeout verarbeiten
  const results = await Promise.allSettled(
    validItems.map(async (item) => {
      try {
        // 30s Timeout pro Upload
        await Promise.race([processQueueItem(item), timeoutPromise(30000)]);
        return { success: true, id: item.id };
      } catch (error) {
        console.error(`Fehler/Timeout bei Queue-Item ${item.id}:`, error);
        const msg = error instanceof Error ? error.message : String(error);
        if (/403|session denied/i.test(msg)) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        // Item aktualisieren (Retry-Counter erhöhen)
        const updatedItem = { ...item, retries: item.retries + 1 };
        await addQueueItem(updatedItem);
        return { success: false, id: item.id };
      }
    })
  );

  // Ergebnisse auswerten
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    const item = validItems[i];

    if (result.status === "fulfilled" && result.value.success) {
      successful.push(item.id);
      // Erfolgreiche Items aus IndexedDB entfernen
      await removeQueueItem(item.id);
    } else {
      failed.push(item.id);
    }
  }

  // Background Sync registrieren, wenn Browser es unterstützt
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register("syncQueue");
    } catch (error) {
      console.error("Background Sync konnte nicht registriert werden:", error);
    }
  }

  notifyProjectStatusRefresh(successful);

  return { successful, failed };
};

/**
 * Einzelnes Queue-Item verarbeiten (intern)
 */
async function processQueueItem(item: QueueItem): Promise<void> {
  switch (item.type) {
    case "image_upload":
      await processImageUpload(item);
      break;

    case "document_upload":
      // Dokument-Upload (z.B. von PhaseScreen)
      await processDocumentUpload(item);
      break;

    case "checklist_update":
      await processChecklistUpdate(item);
      break;

    case "make_pdf_upload":
      await processMakePdfUpload(item);
      break;

    case "abschlussbericht_submit":
      await processAbschlussberichtSubmit(item);
      break;

    case "abschlussbericht_draft_upsert":
      await processAbschlussberichtDraftUpsert(item);
      break;

    case "projektdokumentation_upsert":
      await processProjektdokumentationUpsert(item);
      break;

    case "projektdokumentation_status_update":
      await processProjektdokumentationStatusUpdate(item);
      break;

    default:
      throw new Error(`Unbekannter Queue-Item-Typ: ${item.type}`);
  }
}

/**
 * Formular-PDF via Make.com verarbeiten
 *
 * Kontext:
 * - Das Formular wird im Modal ausgefüllt.
 * - Wenn offline: HTML wird in IndexedDB gequeued.
 * - Wenn online: Dieser Worker sendet den Payload an Make und speichert fileId im Store.
 */
async function processMakePdfUpload(item: QueueItem): Promise<void> {
  const {
    dealId,
    documentTitle,
    htmlContent,
    filename,
    phaseId,
    documentId,
    pdfFields,
    signatures,
    signatureMissing,
    isPdfForm,
    formDataInputs,
    formDataProperty,
  } = item.data || {};

  if (!filename || !documentTitle) {
    throw new Error("Unvollständige Make-PDF-Daten");
  }
  if (isPdfForm && !pdfFields) {
    throw new Error("VDZ: pdf_fields fehlen");
  }
  if (!isPdfForm && !htmlContent) {
    throw new Error("HTML Route: htmlContent fehlt");
  }

  const payload: any = {
    deal_id: dealId || "unknown",
    document_title: documentTitle,
    filename,
    html_content: htmlContent || "",
    is_pdf_form: isPdfForm ?? false,
    signature_missing: signatureMissing ?? false,
    ...(isPdfForm && { pdf_fields: pdfFields, signatures: signatures || {} }),
    ...(formDataInputs && { form_data_inputs: formDataInputs }),
    ...(formDataProperty && { form_data_property: formDataProperty }),
  };

  const response = await fetch(MAKE_PDF_WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Make webhook failed (${response.status})`);
  }

  let fileId: string | undefined;
  try {
    const json = await response.json();
    // Make liefert je nach Scenario unterschiedliche Feldnamen (z.B. data.file_id)
    const candidate: unknown =
      (json as any)?.fileId ??
      (json as any)?.file_id ??
      (json as any)?.data?.fileId ??
      (json as any)?.data?.file_id;

    if (candidate !== undefined && candidate !== null && `${candidate}`.trim() !== "") {
      fileId = `${candidate}`;
    }
  } catch (e) {
    console.error("[queueService] Make JSON Parse Error:", e);
  }

  if (!fileId) {
    throw new Error("Make response enthält keine fileId");
  }

  // Store-Update, damit die UI sofort "Anzeigen" anzeigen kann.
  if (phaseId && documentId) {
    const { saveDocumentFileId, updateDocumentStatus, saveDocumentData } =
      useDocumentStore.getState();

    saveDocumentFileId(phaseId, documentId, fileId);
    updateDocumentStatus(phaseId, documentId, "completed");

    // Optional: Signed URL direkt cachen (bessere UX beim "Anzeigen")
    try {
      const url = await getSignedUrl(fileId);
      if (url) saveDocumentData(phaseId, documentId, url);
    } catch (e) {
      console.warn("[queueService] Konnte Signed URL nicht cachen:", e);
    }
  }
}

/**
 * Hilfsfunktion: Dateiendung aus Data-URL ermitteln
 */
const getExtensionFromDataUrl = (dataUrl: string): string => {
  const mime = dataUrl.match(/:(.*?);/)?.[1];
  if (mime === "application/pdf") return ".pdf";
  if (mime === "image/png") return ".png";
  if (mime === "image/webp") return ".webp";
  if (mime === "image/heic") return ".heic";
  if (mime === "image/heif") return ".heif";
  return ".jpg"; // Default
};

const getExtensionFromFileName = (fileName?: string): string | null => {
  const match = fileName?.trim().match(/(\.[a-z0-9]{2,5})$/i);
  return match?.[1]?.toLowerCase() || null;
};

const getUploadFileName = (file: QueuedUploadFile): string => {
  const extension = getExtensionFromFileName(file.name) || getExtensionFromDataUrl(file.dataUrl);
  return generateUniqueImageName().replace(".jpg", extension);
};

const getDocumentUploadFiles = (item: QueueItem): QueuedUploadFile[] => {
  const files = Array.isArray(item.data?.files)
    ? item.data.files
        .filter((file: any) => typeof file?.dataUrl === "string" && file.dataUrl.trim().length > 0)
        .map((file: any) => ({
          id: file.id,
          name: file.name,
          type: file.type || file.mimeType,
          size: file.size,
          dataUrl: file.dataUrl,
        }))
    : [];

  if (files.length > 0) return files;
  if (typeof item.data?.imageData === "string" && item.data.imageData.trim().length > 0) {
    return [{ dataUrl: item.data.imageData }];
  }
  return [];
};

const appendUploadedDocumentFile = (
  phaseId: string,
  documentId: string,
  file: UploadedDocumentFile
): UploadedDocumentFile[] => {
  const { appendDocumentFiles } = useDocumentStore.getState();
  return appendDocumentFiles(phaseId, documentId, [file]);
};

/**
 * Bild-Upload verarbeiten – bevorzugt Google Drive, Fallback HubSpot
 */
async function processImageUpload(item: QueueItem): Promise<void> {
  const { imageData, checklistItemId, phaseId, dealName, sharedDrive, gdriveFolderId } = item.data;

  const extension = getExtensionFromDataUrl(imageData);
  const fileName = generateUniqueImageName().replace(".jpg", extension);

  let fileId: string | null | void = null;

  if (dealName) {
    // Google Drive Upload: Ordnerstruktur = dealName / Dokumente / Phasenname
    const phaseDisplayName = resolvePhaseFolderName(phaseId);
    const driveResult = await apiExtensions.uploadToDrive(
      dealName,
      imageData,
      fileName,
      ['Dokumente', phaseDisplayName],
      sharedDrive,
      gdriveFolderId,
    );
    if (driveResult) {
      const { saveDocumentFileId, saveDocumentData } = useDocumentStore.getState();
      saveDocumentFileId(phaseId, checklistItemId, driveResult.fileId);
      saveDocumentData(phaseId, checklistItemId, driveResult.webViewLink);
    }
    return;
  } else {
    // Fallback: HubSpot via Backend (wenn kein dealName übergeben)
    fileId = await apiExtensions.uploadImage(phaseId, checklistItemId, imageData, fileName);
  }

  if (fileId) {
    const { saveDocumentFileId } = useDocumentStore.getState();
    saveDocumentFileId(phaseId, checklistItemId, fileId);
    console.log(`[queueService] Drive FileId gespeichert: ${fileId} für ${checklistItemId}`);
  }
}

/**
 * Dokument-Upload verarbeiten (von PhaseScreen) – bevorzugt Google Drive, Fallback HubSpot
 */
async function processDocumentUpload(item: QueueItem): Promise<void> {
  const { documentId, phaseId, dealName, sharedDrive, gdriveFolderId } = item.data;
  const uploadFiles = getDocumentUploadFiles(item);

  if (uploadFiles.length === 0 || !documentId || !phaseId) {
    throw new Error("Unvollständige Dokument-Upload-Daten");
  }

  const existingFiles = normalizeUploadedDocumentFiles(item.data?.existingFiles);
  if (existingFiles.length > 0) {
    const { appendDocumentFiles } = useDocumentStore.getState();
    appendDocumentFiles(phaseId, documentId, existingFiles);
  }

  for (const uploadFile of uploadFiles) {
    const fileName = getUploadFileName(uploadFile);
    let fileId: string | null | void = null;

    if (dealName) {
      // Google Drive Upload: Ordnerstruktur = dealName / Dokumente / Phasenname
      const phaseDisplayName = resolvePhaseFolderName(phaseId);
      const driveResult = await apiExtensions.uploadToDrive(
        dealName,
        uploadFile.dataUrl,
        fileName,
        ['Dokumente', phaseDisplayName],
        sharedDrive,
        gdriveFolderId,
      );
      if (driveResult) {
        const { saveDocumentData, updateDocumentStatus } = useDocumentStore.getState();
        const mergedFiles = appendUploadedDocumentFile(phaseId, documentId, {
          id: driveResult.fileId,
          fileId: driveResult.fileId,
          url: driveResult.webViewLink,
          name: uploadFile.name || fileName,
          mimeType: uploadFile.type,
          uploadedAt: new Date().toISOString(),
          thumbnailUrl: driveResult.thumbnailLink,
          previewData: uploadFile.dataUrl,
          source: "drive",
        });
        saveDocumentData(phaseId, documentId, driveResult.webViewLink);
        updateDocumentStatus(phaseId, documentId, "completed");

        // Drive-Link nach HubSpot patchen (außer Seriennummer, die hat eigenen Flow)
        if (documentId !== 'seriennummer-ausseneinheit') {
          const dealId = phaseId.split('-')[0];
          const hubspotProperty = DOCUMENT_TO_PROPERTY_MAP[documentId];
          if (dealId && hubspotProperty && driveResult.webViewLink) {
            try {
              const value = phaseId.endsWith('-durchfuehrung')
                ? uploadedDocumentFilesToPropertyValue(mergedFiles)
                : driveResult.webViewLink;
              await updateDealTextProperty(dealId, hubspotProperty, value);
            } catch (e) {
              console.warn('[queueService] HubSpot Drive-Link Patch fehlgeschlagen:', e);
            }
          }
        }
      }
    } else {
      // Fallback: HubSpot via Backend
      fileId = await apiExtensions.uploadImage(phaseId, documentId, uploadFile.dataUrl, fileName);

      if (fileId) {
        const { updateDocumentStatus } = useDocumentStore.getState();
        const uploadedFile = normalizeUploadedDocumentFiles(fileId)[0];
        if (uploadedFile) {
          appendUploadedDocumentFile(phaseId, documentId, {
            ...uploadedFile,
            name: uploadFile.name || fileName,
            mimeType: uploadFile.type,
            previewData: uploadFile.dataUrl,
          });
        }
        updateDocumentStatus(phaseId, documentId, "completed");
        console.log(`[queueService] FileId gespeichert: ${fileId} für ${documentId}`);
      }
    }
  }
}

async function processAbschlussberichtSubmit(item: QueueItem): Promise<void> {
  const {
    projectId,
    phaseId,
    submission,
  }: {
    projectId?: string;
    phaseId?: string;
    submission?: AbschlussberichtSubmission;
  } = item.data || {};

  if (!projectId || !phaseId || !submission) {
    throw new Error("Unvollständige Abschlussbericht-Daten");
  }

  await api.post(`/projects/${projectId}/abschlussbericht-submissions`, submission);

  const { updatePhaseStatus } = useDocumentStore.getState();
  updatePhaseStatus(phaseId, "completed", submission.submittedAt, submission.submittedByEmail);
}

async function processAbschlussberichtDraftUpsert(item: QueueItem): Promise<void> {
  const {
    projectId,
    draft,
  }: {
    projectId?: string;
    draft?: AbschlussberichtDraft;
  } = item.data || {};

  if (!projectId || !draft) {
    throw new Error("Unvollständige Abschlussbericht-Draft-Daten");
  }

  await api.put(`/projects/${projectId}/abschlussbericht-draft`, draft);
}

async function processProjektdokumentationUpsert(item: QueueItem): Promise<void> {
  const {
    projectId,
    payload,
  }: {
    projectId?: string;
    payload?: ProjektdokumentationFormData;
  } = item.data || {};

  if (!projectId || !payload) {
    throw new Error("Unvollständige Projektdokumentation-Daten");
  }

  await api.put(`/projects/${projectId}/projektdokumentation`, payload);
}

async function processProjektdokumentationStatusUpdate(item: QueueItem): Promise<void> {
  const {
    projectId,
    phaseId,
    payload,
  }: {
    projectId?: string;
    phaseId?: string;
    payload?: ProjektdokumentationStatusUpdate;
  } = item.data || {};

  if (!projectId || !payload) {
    throw new Error("Unvollständige Projektdokumentation-Statusdaten");
  }

  const response = await api.post(`/projects/${projectId}/projektdokumentation/status`, payload);

  const existingDeal = useDealsStore.getState().deals.find((deal) => deal.id === projectId);
  if (existingDeal) {
    const existingSnapshot = existingDeal.project_status || null;
    const nextSnapshot = response?.projectStatus
      ? {
          ...(existingSnapshot || {}),
          ...response.projectStatus,
        }
      : existingSnapshot
        ? { ...existingSnapshot }
        : null;
    if (nextSnapshot) {
      nextSnapshot.projektdokumentationApprovalStatus =
        response?.record?.status || nextSnapshot.projektdokumentationApprovalStatus || null;
    }
    useDealsStore.getState().upsertDeal({
      ...existingDeal,
      project_status: nextSnapshot,
    });
  }

  if (payload.status === "approved" && phaseId) {
    const approvedAt = response?.record?.approvedAt || new Date().toISOString();
    useDocumentStore
      .getState()
      .updatePhaseStatus(
        phaseId,
        "completed",
        approvedAt,
        response?.record?.completedByEmail || undefined
      );
  }
}

/**
 * Checklisten-Update verarbeiten
 */
const processChecklistUpdate = async (item: QueueItem): Promise<void> => {
  const { phaseId, checklistItemId, data } = item.data;

  if (!phaseId || !checklistItemId) {
    throw new Error("Unvollständige Checklisten-Update-Daten");
  }

  // Update an API senden
  await api.patch(`/phases/${phaseId}/checklist/${checklistItemId}`, data);
};

/**
 * Data-URL in Blob konvertieren
 */
export const dataUrlToBlob = (dataUrl: string): Blob => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

/**
 * Sync-State aus localStorage löschen (bei Logout)
 */
export const clearSyncState = (): void => {
  try {
    localStorage.removeItem("sync-state");
    localStorage.removeItem("sync-timestamp");
    localStorage.removeItem("sync-last-result");
    console.log("Sync-State cleared");
  } catch (error) {
    console.error("Fehler beim Löschen des Sync-State:", error);
  }
};
